import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import { decodeCityName } from "@overrides/utilities";
import {
  DisruptionAlertBannerType,
  disruptionExerciseFlags,
  DisruptionExerciseProgress,
  setDisruptionExerciseProgress,
} from "@tenant/disruption";
import { DISRUPTION_EXERCISE_PATH_WITH_ITINERARY_ID } from "@/utilities/constants";

export type RbcDisruptionAirportMap = Record<string, { cityName?: string }>;

export const useFlightDisruptionAlertBanner = (
  itinerary: BookedFlightItineraryWithDepartureTime,
  airportMap: RbcDisruptionAirportMap | undefined
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isDisruptionProtectionExercisable, isEligibleToBookReturn } =
    disruptionExerciseFlags(itinerary.ancillaries.disruptionProtection);

  const getOutboundDestinationCity = useCallback(() => {
    const outbound = itinerary.bookedItinerary.travelItinerary.slices[0];
    if (!outbound?.segments?.length) {
      return undefined;
    }
    const outboundDestinationCode =
      outbound.segments[outbound.segments.length - 1].destination.locationCode;
    return decodeCityName(
      airportMap?.[outboundDestinationCode]?.cityName,
      outboundDestinationCode
    );
  }, [itinerary.bookedItinerary.travelItinerary.slices, airportMap]);

  const handleDisruptionProtectionCta = useCallback(() => {
    const shouldProceedToExerciseFlow =
      isDisruptionProtectionExercisable || isEligibleToBookReturn;
    if (shouldProceedToExerciseFlow) {
      dispatch(
        setDisruptionExerciseProgress(DisruptionExerciseProgress.LandingPage)
      );
    } else {
      dispatch(
        setDisruptionExerciseProgress(DisruptionExerciseProgress.NotEligible)
      );
    }
    const id = itinerary.bookedItinerary?.id;
    if (!id) {
      console.error(
        "Cannot navigate to disruption exercise: missing itinerary ID"
      );
      return;
    }
    navigate(DISRUPTION_EXERCISE_PATH_WITH_ITINERARY_ID(id));
  }, [
    dispatch,
    isDisruptionProtectionExercisable,
    isEligibleToBookReturn,
    itinerary.bookedItinerary?.id,
    navigate,
  ]);

  const showDisruptionContractBanner =
    isDisruptionProtectionExercisable || isEligibleToBookReturn;

  const disruptionAlertBannerType = isDisruptionProtectionExercisable
    ? DisruptionAlertBannerType.DISRUPTED
    : isEligibleToBookReturn
      ? DisruptionAlertBannerType.BOOK_RETURN
      : DisruptionAlertBannerType.DISRUPTED;

  const disruptionBannerDestinationProps = useMemo(() => {
    if (!isEligibleToBookReturn) {
      return {};
    }
    return { destination: getOutboundDestinationCity() };
  }, [isEligibleToBookReturn, getOutboundDestinationCity]);

  return {
    handleDisruptionProtectionCta,
    disruptionAlertBannerType,
    disruptionBannerDestinationProps,
    isDisruptionProtectionExercisable,
    isEligibleToBookReturn,
    showDisruptionContractBanner,
  };
};
